import { useState } from "react";

import { Icon } from "~/components/icon";

/**
 * Key/value editor for the tag commands. Renders one row per tag with
 * add/remove controls and mirrors the rows into a hidden `tags` input as a
 * JSON merge-patch object string (keys removed relative to `initialTags`
 * become nulls), so the normal form submit path works.
 */
export function TagsEditor({
  initialTags,
  derivedKeys,
}: {
  initialTags?: Record<string, string> | null;
  /** Derived tag keys (kind, customer); rows using them get a warning. */
  derivedKeys?: string[];
}) {
  // Deletion baseline: keys here with no row left are sent as null on submit.
  // Snapshotted at mount so a tag added elsewhere mid-edit (arriving via the
  // poll) cannot be deleted by a patch from a user who never saw it.
  const [initialKeys] = useState(() => Object.keys(initialTags ?? {}));
  const EMPTY_ROW = { key: "", value: "", initialKey: "" };
  const [rows, setRows] = useState(() => {
    const entries = Object.entries(initialTags ?? {});
    return entries.length > 0
      ? entries.map(([key, value]) => ({ key, value, initialKey: key }))
      : [EMPTY_ROW];
  });

  // Initial keys whose rows were removed are sent as nulls, which deletes
  // them on the backend. A row whose key was merely blanked is a no-op for
  // its original tag, not a delete.
  const patch: Record<string, string | null> = {};
  const duplicateKeys: string[] = [];
  const blankedKeys = new Set<string>();
  for (const r of rows) {
    const key = r.key.trim();
    if (key === "") {
      if (r.initialKey !== "") blankedKeys.add(r.initialKey);
      continue;
    }
    if (key in patch && !duplicateKeys.includes(key)) {
      duplicateKeys.push(key);
    }
    patch[key] = r.value.trim();
  }
  for (const key of initialKeys) {
    if (!(key in patch) && !blankedKeys.has(key)) patch[key] = null;
  }
  const serialized = Object.keys(patch).length > 0 ? JSON.stringify(patch) : "";

  const derivedConflicts = (derivedKeys ?? []).filter((key) => key in patch);
  const hasKeylessValue = rows.some(
    (r) => r.key.trim() === "" && r.value.trim() !== "",
  );

  const updateRow = (
    i: number,
    change: Partial<{ key: string; value: string }>,
  ) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, ...change } : r)),
    );
  const removeRow = (i: number) =>
    setRows((prev) =>
      prev.length === 1 ? [EMPTY_ROW] : prev.filter((_, idx) => idx !== i),
    );
  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW]);

  const inputClass =
    "w-1/2 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="space-y-2">
      <input type="hidden" name="tags" value={serialized} />
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            aria-label="Tag key"
            placeholder="key"
            value={row.key}
            onChange={(e) => updateRow(i, { key: e.target.value })}
            className={inputClass}
          />
          <span className="text-gray-400">=</span>
          <input
            aria-label="Tag value"
            placeholder="value"
            value={row.value}
            onChange={(e) => updateRow(i, { value: e.target.value })}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="rounded p-1 text-gray-400 hover:text-red-600"
            aria-label="Remove tag"
          >
            <Icon name="Trash2" size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
      >
        <Icon name="Plus" size={14} /> Add tag
      </button>
      {duplicateKeys.length > 0 ? (
        <p className="text-xs text-red-600">
          Duplicate key{duplicateKeys.length > 1 ? "s" : ""}:{" "}
          {duplicateKeys.join(", ")} — only the last value is kept.
        </p>
      ) : null}
      {derivedConflicts.length > 0 ? (
        <p className="text-xs text-orange-600">
          {derivedConflicts.join(", ")}{" "}
          {derivedConflicts.length > 1
            ? "are derived tags"
            : "is a derived tag"}{" "}
          and cannot be overridden.
        </p>
      ) : null}
      {hasKeylessValue ? (
        <p className="text-xs text-orange-600">
          Rows without a key are ignored.
        </p>
      ) : null}
    </div>
  );
}
